// ./src/components/generic/ErrorAlert/ErrorAlert.tsx
//
// A prominent, actionable error banner. Reads straight off an OpResult (no
// wrapper type) and gives the user a way to (a) read the full detail,
// (b) copy it, and (c) report it.
//
// The report flow is NOT re-implemented per page: ErrorAlert calls the app-wide
// <ErrorReportProvider> (via useOptionalErrorReport) which opens a single
// pre-filled feedback drawer. A page only tells the alert WHERE it is —
//
//   <ErrorAlert error={lastError} page="Tags" operation="tagList" onDismiss={clearError} />
//
// — and everything else (display, copy, report) is handled here.

import React, { useMemo, useCallback } from "react";
import { Alert, Stack, Text, Group, Button, CopyButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { refusalReason } from "@em/api";

import { useOptionalErrorReport } from "../ErrorReportContext";

import { AppIcon } from "../AppIcon";

export interface ErrorAlertProps {
  /** The failed operation's OpResult. Renders nothing when there's no error. */
  error?: OpResult | null;

  /** Banner title. */
  title?: string;

  /** Page/section where this happened — shown in the copied/reported text. */
  page?: string;

  /** API operation that failed (e.g. "tagList") — included in the report. */
  operation?: string;

  /**
   * Optional override for the report action. When omitted, ErrorAlert uses the
   * app-wide ErrorReportProvider. The Report button hides only when neither is
   * available.
   */
  onReport?: (details: string) => void;

  /** Dismiss handler. When provided, a close button shows. */
  onDismiss?: () => void;
}

/** Flatten an OpResult's errors into readable lines ("field: message"). */
const collectMessages = (error: OpResult): string[] => {
  const items = (error.errors as { name?: string; errors?: string[] }[]) || [];
  const lines: string[] = [];
  for (const item of items) {
    const name = item?.name || "";
    for (const msg of item?.errors || []) {
      lines.push(name ? `${name}: ${msg}` : msg);
    }
  }
  return lines;
};

const ErrorAlertComponent: React.FC<ErrorAlertProps> = ({
  error,
  title,
  page,
  operation,
  onReport,
  onDismiss,
}) => {
  const { t } = useTranslation(["common"]);
  const reporter = useOptionalErrorReport();

  const messages = useMemo(
    () => (error ? collectMessages(error) : []),
    [error]
  );

  // Full, copy/report-friendly detail block.
  const details = useMemo(() => {
    if (!error) return "";
    const parts: string[] = [];
    if (page) parts.push(`Page: ${page}`);
    if (operation) parts.push(`Operation: ${operation}`);
    if (error.code != null) parts.push(`Code: ${error.code}`);
    const body = messages.length ? messages.join("\n") : refusalReason(error);
    parts.push(`Details:\n${body}`);
    return parts.join("\n");
  }, [error, page, operation, messages]);

  const canReport = !!onReport || !!reporter;

  const handleReport = useCallback(() => {
    if (onReport) {
      onReport(details);
    } else {
      reporter?.reportError({ page, operation, details });
    }
  }, [onReport, reporter, page, operation, details]);

  if (!error || !error.hasErrors()) return null;

  const summaryLines = messages.length
    ? messages
    : [
        refusalReason(error) ||
          t("common:errors.generic", { defaultValue: "Something went wrong." }),
      ];

  return (
    <Alert
      color="red"
      variant="light"
      radius="md"
      icon={<AppIcon iconName="AlertTriangle" size={18} />}
      title={
        title || t("common:errors.title", { defaultValue: "Something went wrong" })
      }
      withCloseButton={!!onDismiss}
      onClose={onDismiss}
    >
      <Stack gap="sm">
        <Stack gap={2}>
          {summaryLines.map((line, i) => (
            <Text key={i} size="md" c="red.9" style={{ wordBreak: "break-word" }}>
              {line}
            </Text>
          ))}
        </Stack>

        <Group gap="xs">
          <CopyButton value={details} timeout={2000}>
            {({ copied, copy }) => (
              <Button
                size="md"
                variant="light"
                color={copied ? "teal" : "red"}
                leftSection={
                  <AppIcon iconName={copied ? "Check" : "Copy"} size={14} />
                }
                onClick={copy}
              >
                {copied
                  ? t("common:buttons.copied", { defaultValue: "Copied" })
                  : t("common:buttons.copyDetails", {
                      defaultValue: "Copy details",
                    })}
              </Button>
            )}
          </CopyButton>

          {canReport && (
            <Button
              size="md"
              variant="filled"
              color="red"
              leftSection={<AppIcon iconName="Message" size={14} />}
              onClick={handleReport}
            >
              {t("common:buttons.reportIssue", { defaultValue: "Report issue" })}
            </Button>
          )}
        </Group>
      </Stack>
    </Alert>
  );
};

export const ErrorAlert = React.memo(ErrorAlertComponent);
